import { computed, signal } from '@angular/core';
import { take } from 'rxjs/operators';
import { ApolloService } from '../../../../core/services/apollo.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { Notification } from '../../entities/notification/notification';
import * as fromGraphQl from './graphql';

/** Die zwei Segmente der Oberflaeche. i18n-Keys heissen historisch `tabs.*` — dasselbe gemeint. */
export type NotificationSegment = 'unread' | 'all';

/**
 * Wie viele leere Seiten mit gesetztem Cursor am Stueck ueberbrueckt werden (Client-Dok §2).
 * Notbremse gegen einen Server, der endlos leere Seiten liefert — kein fachlicher Grenzwert.
 */
const MAX_EMPTY_PAGE_HOPS = 5;

/**
 * Was app-notification an einer Zeile ausloest. NotificationListStore erfuellt das fuer Popover und
 * /notifications; dashboard-announcements.component erfuellt dieselbe Schnittstelle fuer die
 * Dashboard-Ankuendigungen, die ueber eine eigene, kleinere Query geladen werden statt ueber diesen
 * Store. app-notification selbst kennt nur die Schnittstelle, nicht die konkrete Datenquelle.
 */
export interface NotificationRowActions {
  reportOpened(id: string): void;
  markRead(id: string): void;
  deleteNotification(id: string): void;
}

/**
 * Eine Benachrichtigungsliste (#736 §2, Entscheidung 12).
 *
 * Bewusst eine schlichte Klasse und kein `@Injectable`: es gibt davon ZWEI Instanzen mit
 * verschiedenen Seitengroessen — eine im NotificationService fuer das Popover, eine in der
 * Seitenkomponente. Ein gemeinsamer Zustand wuerde sich gegenseitig ueberschreiben: die Seite laedt
 * nach, der Nutzer oeffnet die Glocke, und der Nachladestand waere weg.
 *
 * Der Zaehler ist NICHT Sache dieser Klasse — sie meldet ueber `onReadStateChanged` nur, dass sich
 * am Gelesen-Zustand etwas geaendert hat.
 */
export class NotificationListStore implements NotificationRowActions {

  constructor(
    private readonly apolloService: ApolloService,
    private readonly toastService: ToastService,
    private readonly pageSize: number,
    private readonly onReadStateChanged: () => void
  ) {}

  private readonly _segment = signal<NotificationSegment>('unread');
  private readonly _items = signal<Notification[]>([]);
  private readonly _nextCursor = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _error = signal(false);

  readonly segment = this._segment.asReadonly();
  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loadingMore = this._loadingMore.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasMore = computed(() => this._nextCursor() !== null);

  /**
   * `fetchPolicy: 'network-only'` ist Pflicht: `popoverList` ist eine sitzungsweite Singleton-Instanz
   * (NotificationService), die bei jedem Oeffnen der Glocke dieselben Variablen erneut abfragt. Ohne
   * die explizite Policy greift Apollos Default fuer `query()` (`cache-first`) — das erste Ergebnis
   * dieser Variablenkombination wuerde dann fuer den Rest der Session eingefroren zurueckgegeben,
   * unabhaengig davon, wie oft der Zaehler an der Glocke zwischenzeitlich neue Werte pollt.
   */
  loadFirstPage(): void {
    this._loading.set(true);
    this._error.set(false);
    this.fetch(null, true, 0);
  }

  /** Ein Segmentwechsel ist eine neue Query mit passendem unreadOnly — kein Client-Filtern. */
  setSegment(segment: NotificationSegment): void {
    this._segment.set(segment);
    this._nextCursor.set(null);
    this.loadFirstPage();
  }

  loadMore(): void {
    const cursor = this._nextCursor();
    if (!cursor || this._loadingMore() || this._loading()) {
      return;
    }
    this._loadingMore.set(true);
    this.fetch(cursor, false, 0);
  }

  /**
   * Eine Seite holen — und, wenn noetig, gleich die naechste hinterher.
   *
   * Client-Dok §2 ("Eine Paginierungs-Eigenheit"): eine Seite kann WENIGER Eintraege liefern als
   * `limit`, im Grenzfall gar keine, und trotzdem ein gesetztes `nextCursor` tragen — der Server
   * bestimmt den Cursor, BEVOR er eine unaufloesbare Ankuendigungszeile aus der Seite wirft. Wer
   * beim leeren `items` anhaelt, hoert zu frueh auf. Das ist eine Eigenschaft des Cursor-Protokolls
   * und gehoert deshalb hierher und nicht in die Oberflaeche: der Infinite Scroll des Popovers
   * bekaeme sonst nie ein Scroll-Ereignis, das ihn wieder anstupst, und die Liste haengt.
   *
   * `emptyHops` deckelt das auf MAX_EMPTY_PAGE_HOPS aufeinanderfolgende Leerseiten. Ein Server, der
   * unbegrenzt leere Seiten mit Cursor liefert, wuerde den Client sonst endlos durch den Bestand
   * jagen — lieber sichtbar zu wenig anzeigen als eine Endlosschleife.
   *
   * Die Ladeflaggen bleiben ueber alle Spruenge hinweg gesetzt: fuer den Nutzer ist das EIN
   * Ladevorgang, kein Flackern zwischen leerer Liste und Spinner.
   */
  private fetch(cursor: string | null, initial: boolean, emptyHops: number): void {
    const variables = {
      ...(cursor ? { cursor } : {}),
      limit: this.pageSize,
      unreadOnly: this._segment() === 'unread'
    };

    this.apolloService
      .query<fromGraphQl.NotificationsPageResponse>(
        fromGraphQl.NOTIFICATIONS_PAGE_QUERY,
        variables,
        { fetchPolicy: 'network-only' }
      )
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          const page = result?.data?.getMyNotificationsPage;
          const items = page?.items ?? [];
          const next = page?.nextCursor ?? null;

          if (initial && emptyHops === 0) {
            this._items.set(items);
          } else {
            this._items.update((current) => [...current, ...items]);
          }
          this._nextCursor.set(next);

          if (items.length === 0 && next !== null) {
            if (emptyHops < MAX_EMPTY_PAGE_HOPS) {
              this.fetch(next, initial, emptyHops + 1);
              return;
            }
            console.warn(
              'notification-list.store: nach', MAX_EMPTY_PAGE_HOPS,
              'leeren Seiten mit gesetztem Cursor abgebrochen'
            );
          }

          this._loading.set(false);
          this._loadingMore.set(false);
        },
        error: () => {
          // Beim Nachladen bleiben die bestehenden Zeilen stehen und es gibt keinen Fehlerzustand —
          // der naechste Scroll versucht es erneut. Nur das Erstladen hat nichts zu zeigen.
          this._loadingMore.set(false);
          if (initial) {
            this._loading.set(false);
            this._error.set(true);
          }
        }
      });
  }

  /**
   * Wegwischen ohne Oeffnen. Bei Erfolg wird `readAt` in der bestehenden Zeile gesetzt, die Zeile
   * aber NICHT entfernt.
   *
   * markNotificationRead liefert bei einer nicht mehr existierenden oder fremden Notification kein
   * `false`, sondern eine AccessDeniedException — beide Faelle sind nicht unterscheidbar. Bei 7
   * Tagen TTL und offenem Popover ist ein veralteter Listenstand real: Fehler schlucken, Zeile
   * entfernen, Aenderung melden, kein Fehler-Toast.
   */
  markRead(id: string): void {
    // Sofort, nicht erst in `next` — siehe Begruendung an reportOpened. Ein readAt-Patch laesst den
    // DOM-Knoten stehen; nur das ENTFERNEN muss auf die Antwort warten.
    this.patchReadAt(id);

    this.apolloService
      .mutateObservable<fromGraphQl.MarkReadResponse>(
        fromGraphQl.MARK_NOTIFICATION_READ_MUTATION,
        { id }
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.onReadStateChanged();
        },
        error: () => {
          this.removeItem(id);
          this.onReadStateChanged();
        }
      });
  }

  /**
   * Der Nutzer hat die Nachricht ueber die Glocke tatsaechlich geoeffnet (#736 §7).
   *
   * `notificationOpened` setzt `readAt` serverseitig mit — `markNotificationRead` wird deshalb
   * NICHT zusaetzlich gerufen (Client-Dok §3).
   *
   * Der Patch geschieht SOFORT, nicht erst in `next`: die Navigation folgt im selben Klick und die
   * Zeile soll nicht erst nach dem Roundtrip umspringen. Gefahrlos, weil ein readAt-Patch den
   * DOM-Knoten stehen laesst — nur das ENTFERNEN einer Zeile duerfte nicht synchron passieren, sonst
   * ist das Klick-Target beim Dismiss-Check von p-overlayPanel bereits detached.
   *
   * `false` heisst: ueber diesen Kanal ging nie etwas hinaus. Kein Fehler, nicht wiederholen, nichts
   * anzeigen — seit der Stream aus der SENT-Zustellung kommt, fuer IN_APP praktisch ausgeschlossen.
   */
  reportOpened(id: string): void {
    this.patchReadAt(id);

    this.apolloService
      .mutateObservable<fromGraphQl.NotificationOpenedResponse>(
        fromGraphQl.NOTIFICATION_OPENED_MUTATION,
        { id, channel: 'IN_APP' as fromGraphQl.NotificationChannelKey }
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.onReadStateChanged();
        },
        error: () => {
          this.removeItem(id);
          this.onReadStateChanged();
        }
      });
  }

  markAllRead(): void {
    this.apolloService
      .mutateObservable<fromGraphQl.MarkAllReadResponse>(
        fromGraphQl.MARK_ALL_NOTIFICATIONS_READ_MUTATION,
        {}
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loadFirstPage();
          this.onReadStateChanged();
        },
        error: () => {
          this.toastService.addErrorToast('notificationCenterComponent.markAllReadErrorMessage');
        }
      });
  }

  /**
   * Entfernt den Eintrag aus dem eigenen Stream (Client-Dok §3) — der staerkere Lesezustand.
   * Setzt serverseitig `deleted_at` und, falls noch leer, `read_at`; `markNotificationRead` wird
   * deshalb NICHT zusaetzlich gerufen (§11). Es gibt kein Wiederherstellen.
   *
   * Die Zeile verschwindet sofort. Anders als beim Melden ist ein verlorener Aufruf hier fuer den
   * Nutzer sichtbar: er hat die Zeile weggehen sehen, und der naechste Ladevorgang holt sie zurueck.
   * Deshalb wird nicht jeder Fehler geschluckt — aber es sind zwei verschiedene Faelle:
   *
   * - GraphQL-Fehler (AccessDeniedException): der Eintrag existiert nicht mehr, meist hat die TTL
   *   ihn geraeumt. Die entfernte Zeile ist dann richtig, es gibt nichts zu melden (§10-Tabelle).
   * - Netzwerkfehler: das Loeschen kam nie an. Die Zeile kommt wieder, also Toast (§10-Fliesstext).
   */
  deleteNotification(id: string): void {
    this.removeItem(id);

    this.apolloService
      .mutateObservable<fromGraphQl.DeleteNotificationResponse>(
        fromGraphQl.DELETE_NOTIFICATION_MUTATION,
        { id }
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.onReadStateChanged();
        },
        error: (error) => {
          if (error?.networkError) {
            this.toastService.addErrorToast('notificationComponent.deleteErrorMessage');
          }
          this.onReadStateChanged();
        }
      });
  }

  patchReadAt(id: string): void {
    this._items.update((items) =>
      items.map((notification) =>
        notification.id === id
          ? { ...notification, readAt: notification.readAt ?? new Date().toISOString() }
          : notification
      )
    );
  }

  removeItem(id: string): void {
    this._items.update((items) => items.filter((notification) => notification.id !== id));
  }
}
